"use client";

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./Navbar.css";
import logo from "../../../assets/images/medilink.svg";

export default function Navbar() {
  const { t, i18n } = useTranslation("landingPage");

  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  const currentLang = i18n.language?.split("-")[0] || "en";

  const changeLanguage = () => {
    const nextLang =
      currentLang === "en" ? "fr" : currentLang === "fr" ? "ar" : "en";

    i18n.changeLanguage(nextLang);
    localStorage.setItem("medilink-lang", nextLang);

    document.documentElement.setAttribute("lang", nextLang);
    document.documentElement.setAttribute(
      "dir",
      nextLang === "ar" ? "rtl" : "ltr"
    );
  };

  useEffect(() => {
    const savedTheme = localStorage.getItem("medilink-theme");

    if (savedTheme === "dark") {
      document.body.classList.add("dark-mode");
      setDarkMode(true);
    } else {
      document.body.classList.remove("dark-mode");
      setDarkMode(false);
    }
  }, []);

  const LANGS = [
    { code: "en", label: "English" },
    { code: "fr", label: "Français" },
    { code: "ar", label: "العربية" },
  ];

  const current = (i18n.language || "en").split("-")[0];
  const currentIdx = LANGS.findIndex((l) => l.code === current);
  const displayedLang = currentIdx >= 0 ? LANGS[currentIdx] : LANGS[0];

  const nextLang = () => {
    const nextIdx = (currentIdx + 1) % LANGS.length;
    i18n.changeLanguage(LANGS[nextIdx].code);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);

    window.addEventListener("scroll", onScroll);
    onScroll();

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleDarkMode = () => {
    const nextMode = !darkMode;
    setDarkMode(nextMode);

    if (nextMode) {
      document.body.classList.add("dark-mode");
      localStorage.setItem("medilink-theme", "dark");
    } else {
      document.body.classList.remove("dark-mode");
      localStorage.setItem("medilink-theme", "light");
    }
  };

  const links = [
    {
      label: t("navbar.problem"),
      href: "#problem",
    },
    {
      label: t("navbar.platform"),
      href: "#platform",
    },
    {
      label: t("navbar.features"),
      href: "#workflows",
    },
  ];

  const darkIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M21 12.79C20.8427 14.4922 20.2039 16.1144 19.1582 17.4653C18.1126 18.8163 16.7025 19.8403 15.0945 20.4178C13.4865 20.9953 11.7461 21.1024 10.079 20.7266C8.41187 20.3508 6.88462 19.5079 5.67863 18.3019C4.47264 17.0959 3.62975 15.5686 3.25394 13.9015C2.87814 12.2344 2.98527 10.494 3.56278 8.886C4.14028 7.27799 5.16429 5.86792 6.51524 4.82226C7.8662 3.77661 9.48837 3.13778 11.1906 2.98047C10.1948 4.32713 9.71539 5.98671 9.83994 7.65714C9.96449 9.32758 10.6847 10.8984 11.8718 12.0855C13.0589 13.2726 14.6297 13.9928 16.3002 14.1173C17.9706 14.2419 19.6302 13.7625 20.9768 12.7667L21 12.79Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const sunIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path
        d="M12 4V2M12 22V20M4.93 4.93L3.51 3.51M20.49 20.49L19.07 19.07M4 12H2M22 12H20M4.93 19.07L3.51 20.49M20.49 3.51L19.07 4.93M12 17C14.7614 17 17 14.7614 17 12C17 9.23858 14.7614 7 12 7C9.23858 7 7 9.23858 7 12C7 14.7614 9.23858 17 12 17Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );

  return (
    <header className={`med-navbar ${scrolled ? "med-navbar--scrolled" : ""}`}>
      <div className="med-navbar__inner">
        <a href="#home" className="med-navbar__logo">
          <img src={logo} alt="Medilink" />
        </a>

        <nav className="med-navbar__links">
          {links.map((link) => (
            <a key={link.href} href={link.href} className="med-navbar__link">
              {link.label}
            </a>
          ))}
        </nav>

        <div className="med-navbar__actions">
          <button
            className="med-navbar__icon-btn"
            type="button"
            onClick={changeLanguage}
            aria-label="Change language"
            title={`Language: ${currentLang.toUpperCase()}`}
          >
            {currentLang.toUpperCase()}
          </button>

          <button
            className="med-navbar__icon-btn"
            type="button"
            onClick={toggleDarkMode}
            aria-label="Toggle dark mode"
          >
            {darkMode ? sunIcon : darkIcon}
          </button>

          <a href="#cta" className="med-navbar__cta-btn">
            {t("common.getStarted")}
            <span className="med-navbar__cta-circle">→</span>
          </a>
        </div>

        <button
          className={`med-navbar__burger ${menuOpen ? "open" : ""}`}
          type="button"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Open menu"
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      {menuOpen && (
        <div className="med-navbar__mobile-menu">
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="med-navbar__mobile-link"
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
            </a>
          ))}

          <div className="med-navbar__mobile-actions">
            <button
              className="med-navbar__mobile-icon"
              type="button"
              onClick={changeLanguage}
            >
              {currentLang.toUpperCase()}
            </button>

            <button
              className="med-navbar__mobile-icon"
              type="button"
              onClick={toggleDarkMode}
            >
              {darkMode ? sunIcon : darkIcon}
            </button>
          </div>

          <a
            href="#cta"
            className="med-navbar__mobile-cta"
            onClick={() => setMenuOpen(false)}
          >
            {t("common.getStarted")}
            <span className="med-navbar__mobile-cta-circle">→</span>
          </a>
        </div>
      )}
    </header>
  );
}